import { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api, inr } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus, LogOut, Package, Image as ImageIcon, Tag, Users, ShoppingCart, Settings as SettingsIcon } from 'lucide-react';

const EMPTY_PRODUCT = { name: '', sku: '', category: 'Kitchen - Base Units', subcategory: '', size: '', description: '', price_eco: 0, price_prime: 0, price_gold: 0, image_url: '', stock: 100, is_featured: false };

function ProductForm({ initial, onSave, onCancel }) {
    const [f, setF] = useState(initial || EMPTY_PRODUCT);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
    return (
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
                <div><Label>Name</Label><Input value={f.name} onChange={(e) => set('name', e.target.value)} className="mt-1 rounded-none" data-testid="pf-name" /></div>
                <div><Label>SKU</Label><Input value={f.sku} onChange={(e) => set('sku', e.target.value)} className="mt-1 rounded-none" data-testid="pf-sku" /></div>
                <div>
                    <Label>Category</Label>
                    <Select value={f.category} onValueChange={(v) => set('category', v)}>
                        <SelectTrigger className="mt-1 rounded-none" data-testid="pf-category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {['Kitchen - Base Units','Kitchen - Wall Units','Kitchen - Tall Units','Wardrobe','Other Units'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div><Label>Sub-category</Label><Input value={f.subcategory || ''} onChange={(e) => set('subcategory', e.target.value)} className="mt-1 rounded-none" data-testid="pf-subcategory" placeholder="e.g. Dresser, TV Unit" /></div>
                <div><Label>Size</Label><Input value={f.size} onChange={(e) => set('size', e.target.value)} className="mt-1 rounded-none" data-testid="pf-size" /></div>
                <div><Label>Stock</Label><Input type="number" value={f.stock} onChange={(e) => set('stock', Number(e.target.value))} className="mt-1 rounded-none" data-testid="pf-stock" /></div>
                <div><Label>Price · Eco</Label><Input type="number" value={f.price_eco} onChange={(e) => set('price_eco', Number(e.target.value))} className="mt-1 rounded-none" data-testid="pf-price-eco" /></div>
                <div><Label>Price · Prime</Label><Input type="number" value={f.price_prime} onChange={(e) => set('price_prime', Number(e.target.value))} className="mt-1 rounded-none" data-testid="pf-price-prime" /></div>
                <div><Label>Price · Gold</Label><Input type="number" value={f.price_gold} onChange={(e) => set('price_gold', Number(e.target.value))} className="mt-1 rounded-none" data-testid="pf-price-gold" /></div>
            </div>
            <div><Label>Image URL</Label><Input value={f.image_url} onChange={(e) => set('image_url', e.target.value)} className="mt-1 rounded-none" data-testid="pf-image" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={f.description} onChange={(e) => set('description', e.target.value)} className="mt-1 rounded-none" data-testid="pf-desc" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={f.is_featured} onChange={(e) => set('is_featured', e.target.checked)} data-testid="pf-featured" /> Featured on homepage
            </label>
            <div className="flex gap-3 pt-2">
                <Button onClick={() => onSave(f)} className="bg-blue-900 hover:bg-blue-950 rounded-none" data-testid="pf-save">Save</Button>
                <Button variant="outline" onClick={onCancel} className="rounded-none" data-testid="pf-cancel">Cancel</Button>
            </div>
        </div>
    );
}

function ProductsTab() {
    const [items, setItems] = useState([]);
    const [modal, setModal] = useState({ open: false, product: null });

    const load = () => api.get('/products', { params: { limit: 500 } }).then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async (data) => {
        try {
            if (data.id) await api.put(`/admin/products/${data.id}`, data);
            else await api.post('/admin/products', data);
            toast.success('Product saved');
            setModal({ open: false, product: null }); load();
        } catch { toast.error('Save failed'); }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete ${p.name}?`)) return;
        await api.delete(`/admin/products/${p.id}`);
        toast.success('Deleted'); load();
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-display text-2xl font-bold">Products ({items.length})</h2>
                <Button onClick={() => setModal({ open: true, product: null })} className="bg-amber-600 hover:bg-amber-700 rounded-none" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1" /> Add product</Button>
            </div>
            <div className="border border-slate-200 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-left">
                        <tr>
                            <th className="p-4 font-semibold">Product</th>
                            <th className="p-4 font-semibold">Category</th>
                            <th className="p-4 font-semibold">Size</th>
                            <th className="p-4 font-semibold">Eco / Prime / Gold</th>
                            <th className="p-4 font-semibold">Stock</th>
                            <th className="p-4 font-semibold w-32">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-product-row-${p.sku}`}>
                                <td className="p-4">
                                    <div className="flex items-center gap-3">
                                        <img src={p.image_url?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${p.image_url}` : p.image_url} alt="" className="w-10 h-10 object-contain product-img-bg" />
                                        <div>
                                            <div className="font-medium">{p.name}</div>
                                            <div className="text-xs text-slate-500 font-mono">{p.sku}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-4">{p.category}</td>
                                <td className="p-4 text-slate-600 text-xs">{p.size}</td>
                                <td className="p-4 text-xs">{inr(p.price_eco)} / {inr(p.price_prime)} / {inr(p.price_gold)}</td>
                                <td className="p-4">{p.stock}</td>
                                <td className="p-4">
                                    <div className="flex gap-2">
                                        <button onClick={() => setModal({ open: true, product: p })} className="p-2 hover:bg-slate-100" data-testid={`edit-${p.sku}`}><Pencil className="w-4 h-4" /></button>
                                        <button onClick={() => remove(p)} className="p-2 hover:bg-red-50 text-red-600" data-testid={`del-${p.sku}`}><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={modal.open} onOpenChange={(v) => setModal({ open: v, product: null })}>
                <DialogContent className="max-w-2xl rounded-none">
                    <DialogHeader><DialogTitle>{modal.product ? 'Edit product' : 'Add product'}</DialogTitle></DialogHeader>
                    <ProductForm initial={modal.product} onSave={save} onCancel={() => setModal({ open: false, product: null })} />
                </DialogContent>
            </Dialog>
        </div>
    );
}

function BannersTab() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ title: '', subtitle: '', image_url: '', link_url: '', position: 0, active: true });
    const load = () => api.get('/banners').then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    const create = async () => {
        try { await api.post('/admin/banners', { ...form, id: crypto.randomUUID() }); toast.success('Banner added'); setForm({ title: '', subtitle: '', image_url: '', link_url: '', position: 0, active: true }); load(); }
        catch { toast.error('Failed'); }
    };
    const remove = async (b) => { await api.delete(`/admin/banners/${b.id}`); load(); };
    return (
        <div>
            <h2 className="font-display text-2xl font-bold mb-6">Homepage Banners</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((b) => (
                        <div key={b.id} className="border border-slate-200 bg-white" data-testid={`banner-${b.id}`}>
                            <img src={b.image_url} alt="" className="w-full aspect-[3/2] object-cover" />
                            <div className="p-4">
                                <div className="font-display font-semibold">{b.title}</div>
                                <div className="text-xs text-slate-500">{b.subtitle}</div>
                                <button onClick={() => remove(b)} className="text-xs text-red-600 mt-3 hover:underline">Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="border border-slate-200 bg-white p-6 h-fit">
                    <div className="font-display font-semibold mb-4">Add banner</div>
                    <div className="space-y-3">
                        <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none" data-testid="bf-title" />
                        <Input placeholder="Subtitle" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="rounded-none" data-testid="bf-sub" />
                        <Input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="rounded-none" data-testid="bf-img" />
                        <Input placeholder="Link URL (optional)" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} className="rounded-none" data-testid="bf-link" />
                        <Button onClick={create} className="bg-blue-900 hover:bg-blue-950 rounded-none w-full" data-testid="bf-save">Add banner</Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CodesTab() {
    const [dc, setDc] = useState([]);
    const [rc, setRc] = useState([]);
    const [d, setD] = useState({ code: '', percent: 10, active: true });
    const [r, setR] = useState({ code: '', percent: 15, partner_name: '', active: true });
    const load = () => Promise.all([api.get('/admin/discounts').then((x) => setDc(x.data)), api.get('/admin/referrals').then((x) => setRc(x.data))]);
    useEffect(() => { load(); }, []);
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
                <h2 className="font-display text-2xl font-bold mb-6">Discount Codes</h2>
                <div className="border border-slate-200 bg-white divide-y">
                    {dc.map((x) => (
                        <div key={x.id} className="p-4 flex justify-between items-center" data-testid={`disc-${x.code}`}>
                            <div><div className="font-mono font-semibold">{x.code}</div><div className="text-xs text-slate-500">{x.percent}% off</div></div>
                            <button onClick={async () => { await api.delete(`/admin/discounts/${x.id}`); load(); }} className="text-red-600 text-sm">Delete</button>
                        </div>
                    ))}
                </div>
                <div className="mt-4 flex gap-2">
                    <Input placeholder="CODE" value={d.code} onChange={(e) => setD({ ...d, code: e.target.value.toUpperCase() })} className="rounded-none" data-testid="disc-code" />
                    <Input type="number" placeholder="%" value={d.percent} onChange={(e) => setD({ ...d, percent: Number(e.target.value) })} className="w-24 rounded-none" data-testid="disc-percent" />
                    <Button onClick={async () => { await api.post('/admin/discounts', d); setD({ code: '', percent: 10, active: true }); load(); toast.success('Added'); }} className="bg-blue-900 hover:bg-blue-950 rounded-none" data-testid="disc-save">Add</Button>
                </div>
            </div>
            <div>
                <h2 className="font-display text-2xl font-bold mb-6">Referral Codes</h2>
                <div className="border border-slate-200 bg-white divide-y">
                    {rc.map((x) => (
                        <div key={x.id} className="p-4 flex justify-between items-center" data-testid={`ref-${x.code}`}>
                            <div><div className="font-mono font-semibold">{x.code}</div><div className="text-xs text-slate-500">{x.partner_name} · {x.percent}%</div></div>
                            <button onClick={async () => { await api.delete(`/admin/referrals/${x.id}`); load(); }} className="text-red-600 text-sm">Delete</button>
                        </div>
                    ))}
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                    <Input placeholder="CODE" value={r.code} onChange={(e) => setR({ ...r, code: e.target.value.toUpperCase() })} className="rounded-none" data-testid="ref-code" />
                    <Input placeholder="Partner" value={r.partner_name} onChange={(e) => setR({ ...r, partner_name: e.target.value })} className="rounded-none" data-testid="ref-partner" />
                    <Input type="number" placeholder="%" value={r.percent} onChange={(e) => setR({ ...r, percent: Number(e.target.value) })} className="rounded-none" data-testid="ref-percent" />
                </div>
                <Button onClick={async () => { await api.post('/admin/referrals', r); setR({ code: '', percent: 15, partner_name: '', active: true }); load(); toast.success('Added'); }} className="bg-blue-900 hover:bg-blue-950 rounded-none mt-2 w-full" data-testid="ref-save">Add referral</Button>
            </div>
        </div>
    );
}

function OrdersTab() {
    const [orders, setOrders] = useState([]);
    useEffect(() => { api.get('/admin/orders').then((r) => setOrders(r.data)); }, []);
    return (
        <div>
            <h2 className="font-display text-2xl font-bold mb-6">Orders ({orders.length})</h2>
            <div className="border border-slate-200 bg-white overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                    <thead className="bg-slate-50 border-b text-left"><tr><th className="p-4">Order</th><th className="p-4">Customer</th><th className="p-4">Items</th><th className="p-4">Total</th><th className="p-4">Status</th><th className="p-4">Payment</th></tr></thead>
                    <tbody>
                        {orders.map((o) => (
                            <tr key={o.id} className="border-b hover:bg-slate-50" data-testid={`order-${o.order_no}`}>
                                <td className="p-4 font-mono text-xs">{o.order_no}</td>
                                <td className="p-4"><div>{o.customer_name}</div><div className="text-xs text-slate-500">{o.phone}</div></td>
                                <td className="p-4">{o.items?.length}</td>
                                <td className="p-4 font-semibold">{inr(o.total)}</td>
                                <td className="p-4"><span className="text-xs uppercase tracking-wider">{o.status}</span></td>
                                <td className="p-4">{o.payment_status === 'paid' ? <span className="text-green-700 text-xs">Paid</span> : <span className="text-slate-500 text-xs">Pending</span>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function LeadsTab() {
    const [leads, setLeads] = useState([]);
    useEffect(() => { api.get('/admin/leads').then((r) => setLeads(r.data)); }, []);
    return (
        <div>
            <h2 className="font-display text-2xl font-bold mb-6">Leads ({leads.length})</h2>
            <div className="border border-slate-200 bg-white divide-y">
                {leads.map((l) => (
                    <div key={l.id} className="p-5" data-testid={`lead-${l.id}`}>
                        <div className="flex justify-between text-sm">
                            <div><strong>{l.name || 'Anonymous'}</strong> · {l.phone} {l.email && `· ${l.email}`}</div>
                            <div className="text-xs text-slate-500 uppercase tracking-wider">{l.source}</div>
                        </div>
                        {l.message && <div className="text-sm text-slate-600 mt-2">{l.message}</div>}
                        <div className="text-xs text-slate-400 mt-2">{new Date(l.created_at).toLocaleString()}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function SettingsTab() {
    const [s, setS] = useState({ whatsapp_number: '', business_email: '', factory_address: '', ga_measurement_id: '', meta_pixel_id: '', shipping_flat: 1500, advance_percent: 10 });
    useEffect(() => { api.get('/settings').then((r) => setS(r.data)); }, []);
    const save = async () => {
        try { await api.put('/admin/settings', s); toast.success('Settings updated'); localStorage.removeItem('kanbr_settings_cache'); }
        catch { toast.error('Save failed'); }
    };
    const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
    return (
        <div className="max-w-2xl">
            <h2 className="font-display text-2xl font-bold mb-6">Site Settings</h2>
            <div className="space-y-4 border border-slate-200 bg-white p-8">
                <div><Label>WhatsApp Number (with country code)</Label><Input value={s.whatsapp_number} onChange={(e) => set('whatsapp_number', e.target.value)} className="mt-1 rounded-none" data-testid="st-wa" /></div>
                <div><Label>Business Email</Label><Input value={s.business_email} onChange={(e) => set('business_email', e.target.value)} className="mt-1 rounded-none" data-testid="st-email" /></div>
                <div><Label>Factory Address</Label><Textarea rows={2} value={s.factory_address} onChange={(e) => set('factory_address', e.target.value)} className="mt-1 rounded-none" data-testid="st-addr" /></div>
                <div className="grid grid-cols-2 gap-4">
                    <div><Label>Google Analytics Measurement ID</Label><Input value={s.ga_measurement_id} onChange={(e) => set('ga_measurement_id', e.target.value)} placeholder="G-XXXXXXX" className="mt-1 rounded-none" data-testid="st-ga" /></div>
                    <div><Label>Meta Pixel ID</Label><Input value={s.meta_pixel_id} onChange={(e) => set('meta_pixel_id', e.target.value)} className="mt-1 rounded-none" data-testid="st-pixel" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div><Label>Shipping (flat ₹)</Label><Input type="number" value={s.shipping_flat} onChange={(e) => set('shipping_flat', Number(e.target.value))} className="mt-1 rounded-none" data-testid="st-ship" /></div>
                    <div><Label>Advance %</Label><Input type="number" value={s.advance_percent} onChange={(e) => set('advance_percent', Number(e.target.value))} className="mt-1 rounded-none" data-testid="st-advance" /></div>
                </div>
                <Button onClick={save} className="bg-blue-900 hover:bg-blue-950 rounded-none mt-4" data-testid="st-save">Save settings</Button>
            </div>
        </div>
    );
}

export default function Admin() {
    const { admin, loading, logout } = useAuth();
    if (loading) return <div className="p-10 text-slate-500">Loading…</div>;
    if (!admin) return <Navigate to="/admin/login" replace />;

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-[1400px] mx-auto px-6 py-5 flex justify-between items-center">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-blue-900 text-white flex items-center justify-center font-display font-bold">K</div>
                        <div>
                            <div className="font-display font-bold text-slate-900">Kanbr Admin</div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Manufacturing Console</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-500">{admin.email}</span>
                        <button onClick={logout} className="flex items-center gap-2 text-slate-700 hover:text-red-600" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Sign out</button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-6 py-10">
                <Tabs defaultValue="products" className="w-full">
                    <TabsList className="bg-white border border-slate-200 rounded-none p-0 h-auto flex flex-wrap gap-0" data-testid="admin-tabs">
                        <TabsTrigger value="products" className="rounded-none data-[state=active]:bg-blue-900 data-[state=active]:text-white px-6 py-3" data-testid="tab-products"><Package className="w-4 h-4 mr-2" /> Products</TabsTrigger>
                        <TabsTrigger value="banners" className="rounded-none data-[state=active]:bg-blue-900 data-[state=active]:text-white px-6 py-3" data-testid="tab-banners"><ImageIcon className="w-4 h-4 mr-2" /> Banners</TabsTrigger>
                        <TabsTrigger value="codes" className="rounded-none data-[state=active]:bg-blue-900 data-[state=active]:text-white px-6 py-3" data-testid="tab-codes"><Tag className="w-4 h-4 mr-2" /> Codes</TabsTrigger>
                        <TabsTrigger value="orders" className="rounded-none data-[state=active]:bg-blue-900 data-[state=active]:text-white px-6 py-3" data-testid="tab-orders"><ShoppingCart className="w-4 h-4 mr-2" /> Orders</TabsTrigger>
                        <TabsTrigger value="leads" className="rounded-none data-[state=active]:bg-blue-900 data-[state=active]:text-white px-6 py-3" data-testid="tab-leads"><Users className="w-4 h-4 mr-2" /> Leads</TabsTrigger>
                        <TabsTrigger value="settings" className="rounded-none data-[state=active]:bg-blue-900 data-[state=active]:text-white px-6 py-3" data-testid="tab-settings"><SettingsIcon className="w-4 h-4 mr-2" /> Settings</TabsTrigger>
                    </TabsList>
                    <TabsContent value="products" className="mt-8"><ProductsTab /></TabsContent>
                    <TabsContent value="banners" className="mt-8"><BannersTab /></TabsContent>
                    <TabsContent value="codes" className="mt-8"><CodesTab /></TabsContent>
                    <TabsContent value="orders" className="mt-8"><OrdersTab /></TabsContent>
                    <TabsContent value="leads" className="mt-8"><LeadsTab /></TabsContent>
                    <TabsContent value="settings" className="mt-8"><SettingsTab /></TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
